import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Input,
  Label,
  Skeleton,
  Badge,
  Toastify,
} from 'lift-ui';
import { Loader2, Eye, PencilLine, RotateCcw } from 'lucide-react';
import StringListEditor from './StringListEditor';
import BuddyOverviewPreview, { type PreviewHighlight } from './BuddyOverviewPreview';
import {
  getOverviewConfig,
  upsertOverviewConfig,
  OVERVIEW_MAX_ICON_LEN,
  OVERVIEW_MAX_ITEM_LEN,
  type OverviewOwnerRef,
  type UpsertOverviewPayload,
} from '../api';

interface OverviewConfigEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ownerRef: OverviewOwnerRef | null;
  buddyName: string;
  buddySlug?: string;
  /** Fired after a successful save so the parent can refresh the list. */
  onSaved?: () => void;
}

interface FormState {
  icon: string;
  popular_tasks: string[];
  sample_prompts: string[];
  pairs_with: string[];
}

const EMPTY_FORM: FormState = {
  icon: '',
  popular_tasks: [],
  sample_prompts: [],
  pairs_with: [],
};

/** Trim, drop blanks — mirrors the backend's list validator so the count the
 *  user sees matches what gets stored. */
const cleanList = (items: string[]): string[] =>
  items.map((s) => s.trim()).filter((s) => s.length > 0);

const OverviewConfigEditor: React.FC<OverviewConfigEditorProps> = ({
  open,
  onOpenChange,
  ownerRef,
  buddyName,
  buddySlug,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [savedForm, setSavedForm] = useState<FormState>(EMPTY_FORM);
  const [highlight, setHighlight] = useState<PreviewHighlight | null>(null);
  const [pane, setPane] = useState<'edit' | 'preview'>('edit');

  useEffect(() => {
    let cancelled = false;
    if (!open || !ownerRef) return;

    setForm(EMPTY_FORM);
    setSavedForm(EMPTY_FORM);
    setHighlight(null);
    setPane('edit');
    setLoading(true);
    getOverviewConfig(ownerRef)
      .then((cfg) => {
        if (cancelled) return;
        setConfigured(cfg.configured);
        const next: FormState = {
          icon: cfg.icon ?? '',
          popular_tasks: cfg.popular_tasks ?? [],
          sample_prompts: cfg.sample_prompts ?? [],
          pairs_with: cfg.pairs_with ?? [],
        };
        setForm(next);
        setSavedForm(next);
      })
      .catch((err) => {
        if (cancelled) return;
        Toastify(
          'error',
          err?.response?.data?.detail || 'Failed to load buddy config'
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, ownerRef]);

  const iconTooLong = form.icon.length > OVERVIEW_MAX_ICON_LEN;
  const hasTooLongItem = [
    ...form.popular_tasks,
    ...form.sample_prompts,
    ...form.pairs_with,
  ].some((s) => s.length > OVERVIEW_MAX_ITEM_LEN);
  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(savedForm),
    [form, savedForm]
  );
  const canSave =
    !loading && !saving && !iconTooLong && !hasTooLongItem && (isDirty || !configured);

  const handleSave = async () => {
    if (!ownerRef || !canSave) return;
    const payload: UpsertOverviewPayload = {
      icon: form.icon.trim() ? form.icon.trim() : null,
      popular_tasks: cleanList(form.popular_tasks),
      sample_prompts: cleanList(form.sample_prompts),
      pairs_with: cleanList(form.pairs_with),
    };
    setSaving(true);
    try {
      await upsertOverviewConfig(ownerRef, payload, buddyName);
      onSaved?.();
      onOpenChange(false);
    } catch {
      // toast already surfaced in the api layer
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (saving) return;
    if (isDirty && !window.confirm('Discard unsaved changes to this config?')) return;
    onOpenChange(false);
  };

  const setHighlightFor =
    (section: PreviewHighlight['section']) => (index: number | null) =>
      setHighlight(index === null ? null : { section, index });

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : requestClose())}>
      <DialogContent className="flex h-[88vh] w-[95vw] max-w-[1200px] flex-col gap-0 overflow-hidden bg-white p-0">
        <DialogHeader className="shrink-0 gap-1 border-b border-gray-200 px-6 py-4 pr-14">
          <DialogTitle className="flex items-center gap-2 pb-0 text-lg">
            Configure “{buddyName}”
            {isDirty && (
              <Badge variant="outline" className="border-amber-300 text-xs text-amber-600">
                Unsaved changes
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {buddySlug ? `${buddySlug} · ` : ''}
            Edit this buddy&apos;s Overview page config — the preview on the right
            updates as you type.
            {!loading && !configured && (
              <span className="ml-1 text-amber-600">
                Not yet configured — you&apos;re creating it.
              </span>
            )}
          </DialogDescription>

          <div className="mt-2 inline-flex w-fit rounded-lg bg-gray-100 p-0.5 lg:hidden">
            {(['edit', 'preview'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setPane(value)}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium capitalize ${
                  pane === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                }`}
              >
                {value === 'edit' ? (
                  <PencilLine className="h-3.5 w-3.5" />
                ) : (
                  <Eye className="h-3.5 w-3.5" />
                )}
                {value}
              </button>
            ))}
          </div>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
          <div
            className={`min-h-0 overflow-y-auto px-6 py-5 lg:border-r lg:border-gray-200 ${
              pane === 'edit' ? 'block' : 'hidden'
            } lg:block`}
          >
            {loading ? (
              <div className="flex flex-col gap-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-semibold text-gray-800">Icon</Label>
                  <p className="-mt-1 text-xs text-gray-500">
                    An emoji, icon key, or image URL.
                  </p>
                  <Input
                    value={form.icon}
                    placeholder="e.g. 🧑‍🍳"
                    onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
                    className={
                      iconTooLong ? 'border-red-400 focus-visible:ring-red-400' : ''
                    }
                  />
                  {iconTooLong && (
                    <p className="text-xs text-red-500">
                      Max {OVERVIEW_MAX_ICON_LEN} characters ({form.icon.length}).
                    </p>
                  )}
                </div>

                <StringListEditor
                  label="Most Popular Tasks"
                  helperText="Common things people ask this buddy to do."
                  items={form.popular_tasks}
                  onChange={(popular_tasks) =>
                    setForm((f) => ({ ...f, popular_tasks }))
                  }
                  onActiveIndexChange={setHighlightFor('popular_tasks')}
                  placeholder="e.g. Draft a creative brief"
                />

                <StringListEditor
                  label="Sample Prompts"
                  helperText="Example prompts shown to get users started."
                  items={form.sample_prompts}
                  onChange={(sample_prompts) =>
                    setForm((f) => ({ ...f, sample_prompts }))
                  }
                  onActiveIndexChange={setHighlightFor('sample_prompts')}
                  placeholder="e.g. Write 3 hook variations for…"
                />

                <StringListEditor
                  label="Pairs With"
                  helperText="Other buddies or tools this one works well alongside."
                  items={form.pairs_with}
                  onChange={(pairs_with) => setForm((f) => ({ ...f, pairs_with }))}
                  onActiveIndexChange={setHighlightFor('pairs_with')}
                  placeholder="e.g. Whisker"
                />
              </div>
            )}
          </div>

          <div
            className={`min-h-0 overflow-y-auto bg-gray-50 px-6 py-5 ${
              pane === 'preview' ? 'block' : 'hidden'
            } lg:block`}
          >
            <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-400">
              <Eye className="h-3.5 w-3.5" />
              Overview page preview
            </div>
            {loading ? (
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <Skeleton className="h-72 w-full rounded-2xl" />
                <Skeleton className="h-72 w-full rounded-2xl" />
              </div>
            ) : (
              <BuddyOverviewPreview
                buddyName={buddyName}
                icon={form.icon}
                popularTasks={form.popular_tasks}
                samplePrompts={form.sample_prompts}
                pairsWith={form.pairs_with}
                highlight={highlight}
              />
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-200 px-6 py-4">
          <Button
            variant="ghost"
            onClick={() => setForm(savedForm)}
            disabled={!isDirty || saving}
            className="flex items-center gap-2 text-gray-500"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={requestClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!canSave}
              className="flex items-center gap-2"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save config
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OverviewConfigEditor;
